import { useEffect, useState } from "react";
import { activityLogAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { TableScroll, Pagination } from "../components/shared/index";
import { MdSearch, MdRefresh, MdVisibility, MdPerson } from "react-icons/md";
const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const ACTION_COLOR = {
  CREATE: { bg:"#D1FAE5", color:"#065F46" },
  UPDATE: { bg:"#DBEAFE", color:"#1E40AF" },
  DELETE: { bg:"#FEE2E2", color:"#991B1B" },
  VIEW:   { bg:"#F3F4F6", color:"#6B7280" },
  LOGIN:  { bg:"#EDE9FE", color:"#5B21B6" },
  LOGOUT: { bg:"#FEF3C7", color:"#92400E" },
};

const ACTION_KH = {
  CREATE: "បង្កើត",
  UPDATE: "កែប្រែ",
  DELETE: "លុប",
  VIEW:   "មើល",
  LOGIN:  "ចូលប្រព័ន្ធ",
  LOGOUT: "ចាកចេញ",
};

const ENTITY_LABEL = {
  Officer:      "មន្ត្រី",
  Document:     "ឯកសារ",
  Meeting:      "ប្រជុំ",
  Announcement: "ការប្រកាស",
  User:         "អ្នកប្រើ",
  Department:   "នាយកដ្ឋាន",
  Position:     "តំណែង",
  Role:         "តួនាទី",
};

const toDisplay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day:"2-digit", month:"2-digit", year:"numeric",
      hour:"2-digit", minute:"2-digit", second:"2-digit",
    });
  } catch { return iso; }
};

function ActionBadge({ action, label }) {
  const { t } = useLanguage();
  const style = ACTION_COLOR[action] || { bg:"#F3F4F6", color:"#6B7280" };
  return (
    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11,
                   fontWeight:700, background:style.bg, color:style.color,
                   fontFamily:KH, whiteSpace:"nowrap" }}>
      {label || t(ACTION_KH[action]) || action || "—"}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {Array(7).fill(0).map((_,i) => (
        <td key={i} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ height:14, background:"var(--border)", borderRadius:4 }} />
        </td>
      ))}
    </tr>
  );
}

function DetailModal({ log, onClose }) {
  const { t } = useLanguage();
  if (!log) return null;
  return (
    <div style={{
      position:"fixed", inset:0,
      background:"rgba(0,0,0,0.45)", zIndex:1000,
      display:"flex", alignItems:"center", justifyContent:"center",
    }} onClick={onClose}>
      <div style={{
        background:"var(--surface)", borderRadius:14, padding:28,
        width:520, maxWidth:"90vw", maxHeight:"80vh",
        overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.25)",
      }} onClick={e => e.stopPropagation()}>

        <div style={{ display:"flex", justifyContent:"space-between",
                      alignItems:"center", marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <ActionBadge action={log.action} label={log.actionLabel} />
            <span style={{ fontSize:14, fontWeight:700, color:"var(--text)",
                           fontFamily:KH }}>
              {ENTITY_LABEL[log.entityType] || log.entityType || "—"}
            </span>
          </div>
          <button onClick={onClose}
            style={{ background:"none", border:"none", cursor:"pointer",
                     fontSize:20, color:MUTED }}>×</button>
        </div>

        {[
          { label:"លេខ Log",             value: log.logId ?? log.id },
          { label:"អ្នកប្រើប្រាស់",      value: log.userNameKh || log.userEmail || log.userId },
          { label:"អ៊ីមែល",               value: log.userEmail },
          { label:"សកម្មភាព",              value: log.actionLabel || log.action },
          { label:"ប្រភេទទិន្នន័យ",      value: `${ENTITY_LABEL[log.entityType] || log.entityType} (${log.entityType})` },
          { label:"លេខ Entity",           value: log.entityId },
          { label:"IP Address",            value: log.ipAddress },
          { label:"Browser",               value: log.userAgent },
          { label:"ថ្ងៃ និងម៉ោង",        value: toDisplay(log.createdAt) },
        ].map((r,i) => (
          <div key={i} style={{ display:"flex", padding:"8px 0",
                                borderBottom:`1px solid ${BORDER}` }}>
            <span style={{ fontSize:12, color:MUTED, minWidth:160,
                           fontFamily:KH, flexShrink:0 }}>{r.label}</span>
            <span style={{ fontSize:13, color:"var(--text)", fontWeight:500,
                           fontFamily:KH }}>{r.value ?? "—"}</span>
          </div>
        ))}

        {log.details && (
          <div style={{ marginTop:14 }}>
            <p style={{ fontSize:11, color:MUTED, marginBottom:8,
                        fontFamily:KH, fontWeight:600 }}>
              {t("ព័ត៌មានលម្អិត (Details)")}
            </p>
            <pre style={{
              background:"#1E293B", color:"#E2E8F0",
              padding:"14px", borderRadius:8,
              fontSize:11, overflowX:"auto",
              lineHeight:1.7, maxHeight:200,
              fontFamily:"monospace",
            }}>
              {(() => {
                try {
                  return JSON.stringify(JSON.parse(log.details), null, 2);
                } catch {
                  return log.details;
                }
              })()}
            </pre>
          </div>
        )}

        <div style={{ marginTop:20, textAlign:"center" }}>
          <button onClick={onClose}
            style={{ padding:"8px 32px", borderRadius:8,
                     border:`1px solid ${BORDER}`, background:"var(--surface)",
                     cursor:"pointer", fontSize:13, fontFamily:KH }}>
            {t("បិទ")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ActivityLog() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [list,        setList]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [actionFilter,setActionFilter]= useState("");
  const [entityFilter,setEntityFilter]= useState("");
  const [selected,    setSelected]    = useState(null);
  const [page,        setPage]        = useState(0);
  const [pageSize,    setPageSize]    = useState(25);
  const [totalPages,  setTotalPages]  = useState(1);
  const [totalItems,  setTotalItems]  = useState(0);
  const [mineOnly,    setMineOnly]    = useState(false);
  const [actionCounts,setActionCounts]= useState(null);

  const loadCounts = async (scopedToMine = mineOnly) => {
    const uid = scopedToMine ? user?.id : undefined;
    const actions = ["CREATE","UPDATE","DELETE","LOGIN"];
    const results = await Promise.allSettled(
      actions.map(a => activityLogAPI.getAll(0, 1, a, undefined, uid))
    );
    const counts = {};
    results.forEach((r, i) => {
      if (r.status === "fulfilled") {
        const d = r.value?.data?.data ?? r.value?.data;
        counts[actions[i]] = d?.totalElements ?? 0;
      }
    });
    setActionCounts(counts);
  };

  useEffect(() => { loadCounts(); }, [mineOnly]);

  const load = async () => {
    setLoading(true);
    const uid = user?.id;
    const fetchLogs = (userId) => activityLogAPI.getAll(
      page, pageSize,
      actionFilter || undefined,
      entityFilter || undefined,
      userId,
    );
    try {
      let res;
      if (mineOnly && uid) {
        res = await fetchLogs(uid);
      } else {
        try {
          res = await fetchLogs(undefined);
        } catch (e) {
          if (!uid) throw e;
          setMineOnly(true);
          return;
        }
      }
      const data = res?.data?.data ?? res?.data;
      let content = data?.content || [];

      if (!mineOnly && uid && content.length === 0 &&
          page === 0 && !actionFilter && !entityFilter) {
        const mineRes  = await fetchLogs(uid);
        const mineData = mineRes?.data?.data ?? mineRes?.data;
        if (mineData?.content?.length) {
          setMineOnly(true);
          return;
        }
      }

      setList(content);
      setTotalPages(data?.totalPages    || 1);
      setTotalItems(data?.totalElements || 0);
    } catch (e) {
      console.error("Load logs:", e);
      setList([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, pageSize, actionFilter, entityFilter, mineOnly]);

  const filtered = list.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [l.userNameKh, l.userEmail, l.action, l.actionLabel,
            l.entityType, String(l.entityId||"")]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", marginBottom:20 }}>
        <div>
          <h3 style={{ fontSize:18, fontWeight:700, color:BROWN,
                       fontFamily:MO, marginBottom:3 }}>
            {t("ប្រវត្តិសកម្មភាព")}
          </h3>
          <p style={{ fontSize:12, color:MUTED, fontFamily:KH }}>
            {t("សរុប:")} <strong style={{ color:BROWN }}>
              {loading ? "..." : totalItems}
            </strong> សកម្មភាព
            {mineOnly && <span style={{ color:BROWN }}> {t("· បង្ហាញតែរបស់ខ្ញុំ")}</span>}
          </p>
        </div>

        <button onClick={() => { setPage(0); load(); loadCounts(); }}
          style={{ padding:"9px 16px", border:`1px solid ${BORDER}`,
                   borderRadius:8, background:"var(--surface)", cursor:"pointer",
                   fontSize:13, fontFamily:KH, color:BROWN,
                   display:"flex", alignItems:"center", gap:6 }}>
          <MdRefresh size={16}/> {t("ផ្ទុកឡើងវិញ")}
        </button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)",
                    gap:12, marginBottom:20 }}>
        {[
          { action:"CREATE", color:"#065F46", bg:"#D1FAE5" },
          { action:"UPDATE", color:"#1E40AF", bg:"#DBEAFE" },
          { action:"DELETE", color:"#991B1B", bg:"#FEE2E2" },
          { action:"LOGIN",  color:"#5B21B6", bg:"#EDE9FE" },
        ].map((s,i) => (
          <div key={i} style={{ background:"var(--surface)", borderRadius:10,
                                 padding:"14px 16px", border:`1px solid ${BORDER}`,
                                 cursor:"pointer",
                                 boxShadow: actionFilter===s.action
                                   ? `0 0 0 2px ${s.color}` : "none" }}
            onClick={() => {
              setActionFilter(actionFilter === s.action ? "" : s.action);
              setPage(0);
            }}>
            <div style={{ display:"flex", justifyContent:"space-between",
                          alignItems:"center" }}>
              <span style={{ padding:"2px 10px", borderRadius:20, fontSize:12,
                             fontWeight:700, background:s.bg, color:s.color,
                             fontFamily:KH }}>
                {ACTION_KH[s.action]}
              </span>
              <span style={{ fontSize:22, fontWeight:700, color:s.color,
                             fontFamily:MO }}>
                {actionCounts ? (actionCounts[s.action] ?? 0) : "..."}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key==="Enter" && load()}
            placeholder={t("ស្វែងរក: អ្នកប្រើ, Entity Type....")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>

        <button onClick={() => { setMineOnly(m => !m); setPage(0); }}
          title={t("បង្ហាញតែសកម្មភាពរបស់ខ្ញុំ")}
          style={{ padding:"9px 14px", borderRadius:8, cursor:"pointer",
                   border:`1px solid ${mineOnly ? BROWN : BORDER}`,
                   background: mineOnly ? BROWN : "var(--surface)",
                   color: mineOnly ? "#fff" : BROWN,
                   fontSize:13, fontFamily:KH, whiteSpace:"nowrap",
                   display:"flex", alignItems:"center", gap:6 }}>
          <MdPerson size={15}/> {t("របស់ខ្ញុំ")}
        </button>

        <select value={actionFilter}
          onChange={e => { setActionFilter(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("សកម្មភាពទាំងអស់")}</option>
          {Object.entries(ACTION_KH).map(([code, kh]) => (
            <option key={code} value={code}>{kh} ({code})</option>
          ))}
        </select>

        <select value={entityFilter}
          onChange={e => { setEntityFilter(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ប្រភេទទិន្នន័យទាំងអស់")}</option>
          {Object.entries(ENTITY_LABEL).map(([k,v]) => (
            <option key={k} value={k}>{v} ({k})</option>
          ))}
        </select>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <th style={{ padding:"12px 16px", textAlign:"left", color:MUTED,
                           fontWeight:500, borderBottom:`1px solid ${BORDER}`,
                           fontSize:12, fontFamily:KH, background:BG,
                           position:"sticky", top:0, zIndex:1 }}>
                {t("លេខ")}
              </th>
              <th style={{ padding:"12px 16px", textAlign:"left", color:MUTED,
                           fontWeight:500, borderBottom:`1px solid ${BORDER}`,
                           fontSize:12, fontFamily:KH, background:BG,
                           position:"sticky", top:0, zIndex:1 }}>
                {t("អ្នកប្រើ")}
              </th>
              <th style={{ padding:"12px 16px", textAlign:"left", color:MUTED,
                           fontWeight:500, borderBottom:`1px solid ${BORDER}`,
                           fontSize:12, fontFamily:KH, background:BG,
                           position:"sticky", top:0, zIndex:1 }}>
                {t("សកម្មភាព")}
              </th>
              <th style={{ padding:"12px 16px", textAlign:"left", color:MUTED,
                           fontWeight:500, borderBottom:`1px solid ${BORDER}`,
                           fontSize:12, fontFamily:KH, background:BG,
                           position:"sticky", top:0, zIndex:1 }}>
                {t("ប្រភេទទិន្នន័យ")}
              </th>
              <th style={{ padding:"12px 16px", textAlign:"left", color:MUTED,
                           fontWeight:500, borderBottom:`1px solid ${BORDER}`,
                           fontSize:12, fontFamily:KH, background:BG,
                           position:"sticky", top:0, zIndex:1 }}>
                Entity ID
              </th>
              <th style={{ padding:"12px 16px", textAlign:"left", color:MUTED,
                           fontWeight:500, borderBottom:`1px solid ${BORDER}`,
                           fontSize:12, fontFamily:KH, background:BG,
                           position:"sticky", top:0, zIndex:1 }}>
                {t("ថ្ងៃ និងម៉ោង")}
              </th>
              <th style={{ padding:"12px 16px", textAlign:"left", color:MUTED,
                           fontWeight:500, borderBottom:`1px solid ${BORDER}`,
                           fontSize:12, fontFamily:KH, background:BG,
                           position:"sticky", top:0, zIndex:1 }}>
                {t("ព័ត៌មានលម្អិត")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4,5].map(i => <SkeletonRow key={i} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7}
                  style={{ padding:"32px", textAlign:"center",
                           color:MUTED, fontSize:13, fontFamily:KH }}>
                  {mineOnly ? "គ្មានប្រវត្តិសកម្មភាពរបស់អ្នក" : "គ្មានប្រវត្តិសកម្មភាព"}
                </td>
              </tr>
            ) : filtered.map(l => (
              <tr key={l.logId ?? l.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <td style={{ padding:"12px 16px", fontSize:12, color:MUTED,
                              fontFamily:"monospace",
                              borderBottom:`1px solid ${BORDER}` }}>
                  #{l.logId ?? l.id}
                </td>
                <td style={{ padding:"12px 16px", fontSize:13, color:"var(--text)",
                              fontFamily:KH, borderBottom:`1px solid ${BORDER}` }}>
                  {l.userNameKh || l.userEmail || l.userId || "—"}
                </td>
                <td style={{ padding:"12px 16px",
                              borderBottom:`1px solid ${BORDER}` }}>
                  <ActionBadge action={l.action} label={l.actionLabel} />
                </td>
                <td style={{ padding:"12px 16px",
                              borderBottom:`1px solid ${BORDER}` }}>
                  <span style={{ padding:"3px 10px", borderRadius:20,
                                 fontSize:11, fontWeight:500,
                                 background:"#F3F4F6", color:"#374151",
                                 fontFamily:KH }}>
                    {ENTITY_LABEL[l.entityType] || l.entityType || "—"}
                  </span>
                </td>
                <td style={{ padding:"12px 16px", fontSize:12, color:MUTED,
                              fontFamily:"monospace",
                              borderBottom:`1px solid ${BORDER}` }}>
                  {l.entityId ?? "—"}
                </td>
                <td style={{ padding:"12px 16px", fontSize:11, color:MUTED,
                              fontFamily:KH, whiteSpace:"nowrap",
                              borderBottom:`1px solid ${BORDER}` }}>
                  {toDisplay(l.createdAt)}
                </td>
                <td style={{ padding:"12px 16px",
                              borderBottom:`1px solid ${BORDER}` }}>
                  <button onClick={() => setSelected(l)}
                    style={{ padding:"4px 10px", borderRadius:6,
                             border:`1px solid ${BORDER}`,
                             background:"var(--surface)", cursor:"pointer",
                             fontSize:12, color:BROWN, fontFamily:KH,
                             display:"flex", alignItems:"center", gap:5 }}>
                    <MdVisibility size={14}/> {t("មើល")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableScroll>

        <Pagination
          page={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      {selected && (
        <DetailModal log={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}